import { flattenFields, getAllFormSchemaFields } from '@core-engine/schema/field-helpers/index.js'
import type { FormSchema, Schema } from '@core-engine/schema/types.js'

export interface SchemaDiffField {
  name: string
  type: string
}

export interface SchemaDiffResult {
  added: SchemaDiffField[]
  removed: SchemaDiffField[]
  changed: Array<{ before: SchemaDiffField; after: SchemaDiffField }>
  customComponents: {
    added: string[]
    removed: string[]
  }
}

function collectFields(schema: Schema | FormSchema): SchemaDiffField[] {
  if ('type' in schema && schema.type === 'form') {
    return getAllFormSchemaFields(schema as FormSchema)
      .filter((field): field is typeof field & { name: string } => Boolean(field.name))
      .map((field) => ({ name: field.name, type: field.type }))
  }

  return flattenFields(schema.fields).map((field) => ({
    name: field.name,
    type: field.type
  }))
}

function collectCustomComponents(schema: Schema | FormSchema): string[] {
  if (schema.type === 'form') {
    return []
  }

  return (schema.columns || [])
    .filter((column) => column.type === 'custom' && column.component)
    .map((column) => column.component as string)
    .sort()
}

export function diffSchemas(
  before: Schema | FormSchema,
  after: Schema | FormSchema
): SchemaDiffResult {
  const beforeFields = new Map(collectFields(before).map((field) => [field.name, field]))
  const afterFields = new Map(collectFields(after).map((field) => [field.name, field]))

  const added: SchemaDiffField[] = []
  const removed: SchemaDiffField[] = []
  const changed: Array<{ before: SchemaDiffField; after: SchemaDiffField }> = []

  for (const [name, beforeField] of beforeFields) {
    const afterField = afterFields.get(name)
    if (!afterField) {
      removed.push(beforeField)
      continue
    }
    if (beforeField.type !== afterField.type) {
      changed.push({ before: beforeField, after: afterField })
    }
  }

  for (const [name, afterField] of afterFields) {
    if (!beforeFields.has(name)) {
      added.push(afterField)
    }
  }

  const beforeComponents = new Set(collectCustomComponents(before))
  const afterComponents = new Set(collectCustomComponents(after))

  return {
    added: added.sort((a, b) => a.name.localeCompare(b.name)),
    removed: removed.sort((a, b) => a.name.localeCompare(b.name)),
    changed: changed.sort((a, b) => a.before.name.localeCompare(b.before.name)),
    customComponents: {
      added: Array.from(afterComponents)
        .filter((name) => !beforeComponents.has(name))
        .sort(),
      removed: Array.from(beforeComponents)
        .filter((name) => !afterComponents.has(name))
        .sort()
    }
  }
}
